import { DoneButton } from 'components/blue-button'
import EditActionRow from 'components/lesson-builder/components/edit-action-row'
import AddImageOverlay from 'components/lesson-builder/components/add-image-overlay'
import React, { useState, useEffect } from 'react'
import 'css/components/lesson-builder/question-types.css'
import 'css/components/lesson-builder/fill-blank.css'
import { Question } from 'types'
import type { FillBlankQuestion } from 'types'

// Type guard for fill-blank questions
const isFillBlankQuestion = (question: Question): question is FillBlankQuestion => {
  return question.type === 'fill-blank'
}

interface FillBlankAnsweringProps {
  question: Question
  answer?: string[]
  onAnswerChange?: (answers: string[]) => void
}

interface FillBlankViewerProps {
  question: Question
  onRemoveImage?: (optionIndex?: number) => void
}

interface FillBlankEditorProps {
  question: Question
  onEdit: (payload: Partial<Question>) => void
  setIsEditing: (editing: boolean) => void
}

export const FillBlankAnswering = ({ 
  question,
  answer,
  onAnswerChange
}: FillBlankAnsweringProps) => {
  const [answers, setAnswers] = useState<string[]>(
    answer ?? new Array(question.question.length - 1).fill('')
  )
  
  // Type guard to ensure this is a fill-blank question
  if (!isFillBlankQuestion(question)) {
    return <div>Invalid question type for fill-blank answering</div>
  }

  const handleAnswerChange = (index: number, value: string) => {
    const newAnswers = [...answers]
    newAnswers[index] = value
    setAnswers(newAnswers)
    if (onAnswerChange) {
      onAnswerChange(newAnswers)
    }
  }

  return (
    <div className="question-type fill-blank">
      <div className="question-text">
        {question.question.map((part, index) => (
          <React.Fragment key={index}>
            <span className="question-part">{part}</span>
            {index < question.question.length - 1 && (
              <>
                {' '}
                <input
                  type="text"
                  className="blank-input"
                  value={answers[index]}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder="Type your answer"
                />
                {' '}
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
} 

/**
 * Fill Blank View Component - displays the question and answer
 * @param {Object} props
 * @param {string[]} props.question
 * @param {string} props.answer
 */
export const FillBlankViewer = ({
  question,
  onRemoveImage,
}: FillBlankViewerProps) => {
  // Type guard to ensure this is a fill-blank question
  if (!isFillBlankQuestion(question)) {
    return <div>Invalid question type for fill-blank viewer</div>
  }

  return (
    <div className="question-type fill-blank">
      {question.imageUrl && (
        <div className="image-remove-wrapper">
          <img className="question-image" src={question.imageUrl} alt="fill blank" />
          {onRemoveImage && <button className="remove-image-btn" onClick={() => onRemoveImage()} title="Remove image">✕</button>}
        </div>
      )}
      
      <div className="question-text">
        {question.question.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {index < question.question.length - 1 && (
              <span className="blank-answer">
                {' '}<strong>{question.answer[index]}</strong>{/^[.,!?;:]/.test((question.question[index + 1] ?? '').trimStart()) ? '' : ' '}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
} 

export const FillBlankEditor = ({ onEdit, setIsEditing, question, setShowFeedback }: FillBlankEditorProps & { setShowFeedback?: (showFeedback: boolean) => void }) => {
  // Remove unused lessonId and editQuestionEduquery
  // Initialize state with default values, will be updated if question is valid
  const [questionParts, setQuestionParts] = useState<string[]>([])
  const [answerParts, setAnswerParts] = useState<string[]>([])
  const [showImageOverlay, setShowImageOverlay] = useState(false)

  // Update state with actual question data if this is the first render with valid data
  useEffect(() => {
    if (isFillBlankQuestion(question)) {
      setQuestionParts(question.question)
      setAnswerParts(question.answer)
    }
  }, [question])

  // Type guard to ensure this is a fill-blank question
  if (!isFillBlankQuestion(question)) {
    return <div>Invalid question type for fill-blank editor</div>
  }

  const handleDone = () => {
    onEdit({
      question: questionParts,
      answer: answerParts
    })
    setIsEditing(false)
  }

  const addBlank = (index: number) => {
    const newParts = [...questionParts]
    const newAnswers = [...answerParts]
    newParts.splice(index + 1, 0, '')
    newAnswers.splice(index, 0, '')
    setQuestionParts(newParts)
    setAnswerParts(newAnswers)
  }

  const removeBlank = (index: number) => {
    if (questionParts.length > 1) {
      const newParts = questionParts.filter((_, i) => i !== index)
      const newAnswers = answerParts.filter((_, i) => i !== index - 1)
      setQuestionParts(newParts)
      setAnswerParts(newAnswers)
    }
  }

  const updatePart = (index: number, value: string) => {
    const newParts = [...questionParts]
    newParts[index] = value
    setQuestionParts(newParts)
  }

  const updateAnswer = (index: number, value: string) => {
    const newAnswers = [...answerParts]
    newAnswers[index] = value
    setAnswerParts(newAnswers)
  }

  const openQuestionImageOverlay = () => {
    setShowImageOverlay(true)
  }

  const handleUploadImage = (url: string) => {
    onEdit({ imageUrl: url })
    // keep overlay open; user will close via Done
  }

  return (
    <div className="question-type fill-blank">
      {question.imageUrl && <img className="question-image" src={question.imageUrl} alt="fill blank" />}
      
      <div className="editor-section">
        <label>Question:</label>
        <div className="question-builder">
          {questionParts.map((part, index) => (
            <div key={index} className="question-part">
              <input
                className="editor-input"
                type="text"
                value={part}
                onChange={(e) => updatePart(index, e.target.value)}
                placeholder="Enter text here"
              />
              {index < questionParts.length - 1 && (
                  <div className="answer-input">
                    <label className="answer-label">Answer:</label>
                    <input
                      className="editor-input answer-field"
                      type="text"
                      value={answerParts[index] || ''}
                      onChange={(e) => updateAnswer(index, e.target.value)}
                      placeholder="Correct answer"
                    />
                    <button 
                      type="button" 
                      className="remove-blank-btn"
                      onClick={() => removeBlank(index + 1)}
                    >
                      ×
                    </button>
                    <button 
                      type="button" 
                      className="add-blank-btn"
                      onClick={() => addBlank(index)}
                    >
                      + Add Blank
                    </button>
                  </div>
              )}
              
            </div>
          ))}
        </div>
        {setShowFeedback && (
          <EditActionRow setShowFeedback={setShowFeedback} onEdit={onEdit} onAddImage={openQuestionImageOverlay} />
        )}
      </div>

      <div className="preview-section">
        <label>Preview:</label>
        <div className="question-preview">
          {questionParts.join('______')}
        </div>
        <div className="answers-preview">
          <strong>Answers:</strong> {answerParts.join(' ______ ')}
        </div>
      </div>

      {showImageOverlay && (
        <AddImageOverlay
          setShowAddImageOverlay={setShowImageOverlay}
          onUpload={handleUploadImage}
          title={'Add Question Image'}
          note={'Note: Only one image can be inserted before this question.'}
        />
      )}
      <DoneButton onClick={handleDone} />
    </div>
  )
}